import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { Lookup } from '@shared/interfaces/lookup';
import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { FormSubmittedService } from '@shared/services/form-submitted.service';
import { LookupUseCacheService } from '@shared/services/lookup-use-cache.service';

@Component({
    selector: 'sas-multiselect-field-type',
    standalone: true,
    imports: [CommonModule, TranslateModule, NgbDropdownModule, FormsModule],
    template: `
        <div class="mb-3 sas-multiselect">
            <label [for]="field.id"
                   [id]="field.id + 'Label'"
                   class="form-label"
                   title="{{helpText}}"
                   [textContent]="field.props.label"
                   translate="{{field.props.label}}">
            </label>
            @if(field.props.required){
            <span> *</span>
            }
            <div [title]="helpText" class="sas-inline-block">
                @if(helpText.length && !isReadonly) {
                <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                }
            </div>
            <div class="btn-group d-inline-block" ngbDropdown autoClose="outside" [open]="status.isOpen">
                <button [id]="field.id"
                        type="button"
                        class="btn btn-outline-secondary sas-multiselect-btn"
                        [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || formSubmittedService.isSubmitted())}"
                        [disabled]="isReadonly"
                        [attr.aria-labelledby]="field.id + 'Label ' + field.id + 'Values'"
                        ngbDropdownToggle>
                <span [id]="field.id + 'Values'"
                      class="sas-multiselect-btn-text"
                      [class.sas-multiselect-novalue]="!typedValues.length">{{ typedValues || ('SAS_MULTISELECT.NO_ITEM_SELECTED' | translate) }}</span>
                </button>
                <ul class="dropdown-menu" ngbDropdownMenu role="listbox" aria-multiselectable="true" [attr.aria-labelledby]="field.id + 'Label'">
                    <li role="presentation" [hidden]="sasDisableFilter">
                        <input type="search" class="form-control" [(ngModel)]="typedFilter"
                               placeholder="Type to filter..."
                               [attr.aria-label]="'SAS_MULTISELECT.FILTER_PLACEHOLDER' | translate: { Default: 'Filter options' }">
                    </li>
                    <li role="presentation" [hidden]="sasRemoveActionBar || items.length === 0">
                        <div>
                        <button type="button" class="btn btn-link" (click)="selectAll()" style="margin-right: 10px">
                            <span class="bi bi-check2" aria-hidden="true"></span> {{'SAS_MULTISELECT.SELECT_ALL' | translate}}
                        </button>
                        <button type="button" class="btn btn-link" (click)="unselectAll()">
                            <span class="bi bi-x" aria-hidden="true"></span> {{'SAS_MULTISELECT.UNSELECT_ALL' | translate}}
                        </button>
                        </div>
                    </li>
                    @for (item of filteredItems; track item.id){
                    <li [class.selected]="item.selected"
                        role="option"
                        [attr.aria-selected]="item.selected"
                        ngbDropdownItem
                        (click)="onModify(item)"
                        (keydown)="onKeydown($event, item)">
                        {{item.name}} <span class="sas-multiselect-checkbox" aria-hidden="true"></span>
                    </li>
                    }
                </ul>
            </div>
            <select name="{{field.name}}"
                    [(ngModel)]="internal"
                    multiple="multiple"
                    required="{{field.props.required}}"
                    [attr.aria-labelledby]="field.id + 'Label'"
                    [hidden]="true">
                @for (item of items; track $index) {
                <option [ngValue]="item.id">{{item.name}}</option>
                }
            </select>
            <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
            </div>
        </div>
    `,
    styleUrl: './sas-multiselect-field-type.component.scss'
})
export class SasMultiselectFieldTypeComponent extends FieldType<FieldTypeConfig> {
    private http = inject(HttpClient);
    private destroyRef = inject(DestroyRef);
    formSubmittedService = inject(FormSubmittedService);
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);
    private lookupUseCacheService = inject(LookupUseCacheService);

    helpText: string = '';
    status: any = { isOpen: false };
    sasOptions: Array<Lookup> = [];
    sasDisableFilter: boolean = false;
    sasRemoveActionBar: boolean = false;
    sasFullModel: boolean = false;
    items: Array<any> = [];
    itemsSorted: Array<any> = [];
    typedValues!: string;
    typedFilter: string = '';
    multiselect!: Array<any>;
    internal!: Array<any>; // number

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    constructor() {
        super();
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.sasDisableFilter = this.field.props['sasDisableFilter'] || false;
        this.sasRemoveActionBar = this.field.props['sasRemoveActionBar'] || false;
        this.sasFullModel = this.field.props['sasFullModel'] || false;

        this.initializeValues();

        // Sync UI when Formly resets the form control externally
        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.initializeValues());

        if (this.field.props['dataSource']?.type === 'api') {
            this.lookupUseCacheService.getOptions(this.field.props['dataSource'].url)
                .subscribe((result: Array<Lookup>) => {
                    this.sasOptions = result;
                    this.configItems();
                });
        }
        else {
            this.sasOptions = [...this.field.props['sasOptions']];
            this.configItems();
        }
    }

    private initializeValues(): void {
        this.markAsPristine();
        const value = Array.isArray(this.formControl.value) ? this.formControl.value : [];
        this.multiselect = this.sasFullModel
            ? value
            : this.itemsSorted.filter(item => value.includes(item.id));
        this.internal = this.getIds(this.multiselect);
        this.typedValues = this.getString(this.multiselect);
        this.items = this.mapItems(this.itemsSorted, this.multiselect);
        const data = this.sasFullModel ? this.multiselect : this.internal;
        this.formControl.setValue([...data], { emitEvent: false });
    }

    private configItems() {
        this.itemsSorted = this.sortOptions(this.sasOptions, this.field.props['sasOptionsSorted']);
        this.items = this.mapItems(this.itemsSorted, this.multiselect);
    }

    getOptions(urlFetch: string): Observable<Array<Lookup>> {
        return this.http.get<Array<Lookup>>(urlFetch);
    }

    private sortOptions(collection: Array<any>, sorted: string) {
        return sorted === 'true'
            ? [...collection]
            : [...collection].sort((a: any, b: any) => a.name.localeCompare(b.name));
    }

    private mapItems(items: Array<any>, model: any = []) {
        return items.map((item: any) => ({
            ...item,
            selected: model.findIndex((elem: any) => elem.id === item.id) >= 0
        }));
    }

    selectAll() {
        this.filteredItems.forEach(item => { item.selected = true; });
        this.multiselect = this.items.filter(item => item.selected);

        this.markAsDirty();
        this.updateData();

        return false;
    }

    unselectAll() {
        this.items.forEach(elem => { elem.selected = false; });
        this.multiselect = [];
        this.markAsDirty();

        return this.updateData();
    }

    updateData() {
        this.internal = this.getIds(this.multiselect);
        const data = this.sasFullModel ? this.multiselect : this.internal;

        this.formControl.setValue([...data], { emitEvent: false });
        this.markAsDirty();
        this.formControl.updateValueAndValidity({ emitEvent: false });

        this.typedValues = this.getString(this.multiselect);

        return false;
    }

    onKeydown(event: KeyboardEvent, item: any) {
        if (event.key === 'Enter') {
            this.onModify(item);
        }
    }

    onModify(elemSelected: any) {
        const indexItem = this.items.findIndex(elem => elem.id === elemSelected.id);

        this.items[indexItem].selected = !this.items[indexItem].selected;

        if (this.items[indexItem].selected) {
            this.multiselect.push(elemSelected);
        }
        else {
            this.multiselect.splice(this.multiselect.findIndex(elem => elem.id === elemSelected.id), 1);
        }
        // this.markAsDirty();

        return this.updateData();
    };

    get filteredItems() {
        return this.items.filter((item: any) => {
            return item.name.toLowerCase().includes(this.typedFilter.toLowerCase())
        });
    }

    private getString(data: any) {
        let res = '';

        data.forEach((elem: any) => {
            res += `${elem.name}, `;
        });

        if (res.length) {
            return res.substring(0, res.length - 2);
        }

        return res;
    }

    private getIds(data: any): Array<any> {
        return Array.isArray(data)
            ? data.map((elem: any) => elem?.id).filter(id => id != null)
            : [];
    }

    private markAsPristine() {
        if (this.field.formControl) {
            this.field.formControl.markAsPristine();
            this.field.formControl.markAsUntouched();
        }
    }

    private markAsDirty() {
        if (this.field.formControl) {
            this.field.formControl.markAsDirty();
            this.field.formControl.markAsTouched();
        }
    }
}
